import React, { memo, useMemo, useState } from 'react';
import { dates, formats } from 'investira.sdk';
import {
  Button,
  Card,
  Collapse,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  Icon,
  IconButton,
  Stack,
  Tooltip,
  Typography,
  Divider
} from 'investira.react.components';
import { BulkContextActions, DataTable } from '../../../components/organisms';
import utils from '../../../utils';

/** Deriva o rótulo visual sem persistir informações concatenadas. */
function pvGetFundoLabel(pFundo) {
  return (
    utils.helpers.getAtivoDescricao(pFundo) ||
    pFundo.ticker ||
    pFundo.ativo_seuid ||
    pFundo.ativo ||
    pFundo.ifeed_ativo_id
  );
}

const ComparadorFundosHistory = memo(({ items, onDelete, onView }) => {
  const [expanded, setExpanded] = useState(false);
  const [rowSelection, setRowSelection] = useState({});
  const [pendingDeleteKeys, setPendingDeleteKeys] = useState([]);
  const xColumns = useMemo(
    () => [
      {
        accessorKey: 'descricao',
        header: 'Comparação',
        enableSorting: false
      },
      {
        accessorKey: 'savedAt',
        header: 'Salva em',
        enableSorting: false,
        Cell: ({ cell }) => {
          return formats.formatDateCustom(dates.toDate(cell.getValue()), 'DD/MM/YYYY HH:mm');
        }
      }
    ],
    []
  );
  const xData = useMemo(
    () =>
      items.map(pItem => ({
        ...pItem,
        descricao: pItem.fundos.map(pvGetFundoLabel).join(' vs ')
      })),
    [items]
  );

  /** Abre a confirmação para uma ou mais linhas do histórico. */
  function pvRequestDelete(pRows = []) {
    const xKeys = pRows.map(pRow => pRow.original?.key || pRow.key).filter(Boolean);

    setPendingDeleteKeys(xKeys);
  }

  /** Confirma a exclusão pendente e limpa a seleção do DataGrid. */
  function pvConfirmDelete() {
    onDelete(pendingDeleteKeys);
    setRowSelection({});
    setPendingDeleteKeys([]);
  }

  return (
    <>
      <Divider />
      <Stack spacing={2} sx={{ py: 1 }}>
        <Typography component="h2" variant="subtitle1" color="textPrimary" sx={{ fontWeight: 600 }}>
          Histórico de comparações
        </Typography>

        <Stack id="comparador-fundos-history-content">
          {xData.length === 0 ? (
            <Typography variant="body2" color="textSecondary" sx={{ py: 2 }}>
              Nenhuma comparação salva.
            </Typography>
          ) : (
            <DataTable
              columns={xColumns}
              data={xData}
              getRowId={pRow => pRow.key}
              enableRowSelection
              enableSorting={false}
              initialState={{ pagination: { pageIndex: 0, pageSize: 25 } }}
              muiPaginationProps={{ rowsPerPageOptions: [5] }}
              state={{ rowSelection }}
              onRowSelectionChange={setRowSelection}
              renderTopToolbarCustomActions={({ table }) => {
                const xSelectedRows = table.getSelectedRowModel().rows;

                return (
                  <BulkContextActions
                    assinatura={false}
                    selectedRows={xSelectedRows}
                    disabled={xSelectedRows.length === 0}
                    onDeleteClick={pvRequestDelete}
                  />
                );
              }}
              displayColumnDefOptions={{ 'mrt-row-actions': { header: '' } }}
              renderRowActions={({ row }) => (
                <Stack direction="row" spacing={1}>
                  <Tooltip title="Visualizar comparação">
                    <IconButton
                      color="primary"
                      aria-label={`Visualizar ${row.original.descricao}`}
                      onClick={() => onView(row.original)}>
                      <Icon iconName="eye" size={21} />
                    </IconButton>
                  </Tooltip>
                  {/* <Tooltip title="Excluir comparação">
                    <IconButton
                      color="error"
                      aria-label={`Excluir ${row.original.descricao}`}
                      onClick={() => pvRequestDelete([row.original])}>
                      <Icon iconName="delete" size={21} />
                    </IconButton>
                  </Tooltip> */}
                </Stack>
              )}
            />
          )}
        </Stack>
      </Stack>

      <Dialog
        open={pendingDeleteKeys.length > 0}
        onClose={() => setPendingDeleteKeys([])}
        maxWidth="xs"
        fullWidth>
        <DialogTitle>Confirmar exclusão</DialogTitle>
        <DialogContent>
          <DialogContentText variant="body2">
            {pendingDeleteKeys.length === 1
              ? 'Tem certeza que deseja excluir esta comparação do histórico?'
              : `Tem certeza que deseja excluir estas ${pendingDeleteKeys.length} comparações do histórico?`}
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button variant="text" size="small" onClick={() => setPendingDeleteKeys([])}>
            Cancelar
          </Button>
          <Button variant="contained" size="small" color="error" onClick={pvConfirmDelete}>
            Excluir
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
});

export default ComparadorFundosHistory;
